import { Injectable, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { Router } from '@angular/router';
import { nanoid } from 'nanoid';
import { TaskSharedActions } from '../../root-store/meta/task-shared.actions';
import { Log } from '../../core/log';
import { IS_ANDROID_WEB_VIEW } from '../../util/is-android-web-view';
import { INBOX_PROJECT } from '../project/project.const';

/**
 * Septaine WS5 #6 — Share-intent JS consumer (Android).
 *
 * Android's `AndroidManifest.xml` already registers CapacitorMainActivity
 * as a share target for ACTION_SEND with text/*. When the OS routes a
 * share into Septaine, Capacitor surfaces the intent via App.addListener
 * 'appUrlOpen' OR (for SEND with EXTRA_TEXT) through the App plugin's
 * initial intent + native bridge.
 *
 * This service:
 *   - Listens for the appUrlOpen event AND a custom 'ukan:share-text' event
 *     dispatched by a tiny native bridge (capacitor plugin author below)
 *   - Creates a new task in the Inbox project with the shared text as
 *     title (first line) and notes (remainder)
 *   - Navigates to /week to make the new task visible
 *
 * Per CLAUDE.md rule #9: log task IDs only, never user-supplied text.
 *
 * Activation: `init()` is called once from the platform bootstrap when
 * IS_ANDROID_WEB_VIEW. Idempotent — multiple init calls register listeners
 * only once.
 */
@Injectable({ providedIn: 'root' })
export class ShareIntentService {
  private readonly _store = inject(Store);
  private readonly _router = inject(Router);

  private _initialized = false;

  /**
   * Wire up listeners. Safe to call multiple times; only the first call
   * registers handlers.
   */
  init(): void {
    if (!IS_ANDROID_WEB_VIEW || this._initialized) return;
    this._initialized = true;

    // Bridge 1: appUrlOpen (Capacitor App plugin) — fires when the app
    // is launched/resumed via an intent. For ACTION_SEND, the bridge
    // exposes the EXTRA_TEXT on a custom event we emit from the native
    // side (see android/app/src/main/java/garden/mario/ukan/CapacitorMainActivity.kt).
    void this._registerAppListener();

    // Bridge 2: a tiny window-level event the native bridge can dispatch
    // synchronously when EXTRA_TEXT was present at launch.
    window.addEventListener('ukan:share-text', (event: Event) => {
      const detail = (event as CustomEvent<{ text: string }>).detail;
      if (detail?.text) {
        this._createInboxTaskFromShare(detail.text);
      }
    });
  }

  private async _registerAppListener(): Promise<void> {
    try {
      // Lazy-import Capacitor App so non-Capacitor builds don't choke.
      const mod = await import('@capacitor/app');
      const App = (
        mod as {
          App?: {
            addListener?: (
              eventName: string,
              cb: (event: { url: string }) => void,
            ) => unknown;
          };
        }
      ).App;
      if (!App?.addListener) return;
      App.addListener('appUrlOpen', (event: { url: string }) => {
        // intent://share?text=... format — extract text if present.
        try {
          const u = new URL(event.url);
          const text = u.searchParams.get('text');
          if (text) this._createInboxTaskFromShare(text);
        } catch {
          /* unparseable URL — skip silently */
        }
      });
    } catch (e) {
      Log.warn({
        id: 'share-intent.app-listener-failed',
        msg: (e as Error).message,
      });
    }
  }

  private _createInboxTaskFromShare(sharedText: string): void {
    const trimmed = sharedText.trim();
    if (!trimmed) return;

    // Split on first newline: line 1 → title, remainder → notes.
    const firstNewline = trimmed.indexOf('\n');
    const title = firstNewline >= 0 ? trimmed.slice(0, firstNewline).trim() : trimmed;
    const notes = firstNewline >= 0 ? trimmed.slice(firstNewline + 1).trim() : '';

    const id = nanoid();
    this._store.dispatch(
      TaskSharedActions.addTask({
        task: {
          id,
          title,
          notes,
          projectId: INBOX_PROJECT.id,
          tagIds: [],
          subTaskIds: [],
          isDone: false,
          timeEstimate: 0,
          timeSpent: 0,
          timeSpentOnDay: {},
          attachments: [],
          // Inbox lands at the top of the inbox by default.
        } as unknown as Parameters<typeof TaskSharedActions.addTask>[0]['task'],
        isAddToBottom: false,
        isAddToBacklog: false,
        workContextId: INBOX_PROJECT.id,
        workContextType: 'PROJECT',
      } as unknown as Parameters<typeof TaskSharedActions.addTask>[0]),
    );

    Log.log({ id: 'share-intent.task-created', taskId: id });

    // Bring the user to /week so they see what was just shared in.
    void this._router.navigateByUrl('/week');
  }
}
